import { faPlus } from "@fortawesome/pro-solid-svg-icons/faPlus";
import { faMinus } from "@fortawesome/pro-solid-svg-icons/faMinus";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import { styled } from "@mui/material/styles";
import { useMixpanel } from "../hooks/useMixpanel";
import { paletteOptions } from "@portal/app/_components/theme/options/palette";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";

const FAQs: { id: string; question: string; answer: string }[] = [
  {
    id: "overview",
    question: "What is Dreambound?",
    answer:
      "Dreambound's platform allows prospective students to find the right " +
      "educational program for them through searching, filtering, and connecting " +
      "with our extensive selection of career & technical education partners.",
  },
  {
    id: "programs",
    question: "What programs do you offer?",
    answer:
      "Dreambound has over 70 programs across healthcare, technology, " +
      "business, and industrial trades. This includes programs such as " +
      "Medical Billing, Cybersecurity, and welding.",
  },
  {
    id: "financial-aid",
    question: "Do you offer financial aid?",
    answer:
      "Some of our schools offer financial aid for those who qualify. Many " +
      "others offer payment plans, where you can pay the cost of class over time.",
  },
  {
    id: "online-classes",
    question: "Is it possible to do online classes?",
    answer:
      "Yes, Dreambound offers many online programs. On Dreambound's search, " +
      "you can filter by online, in-person, and hybrid (part online, part " +
      "in-person).",
  },
  {
    id: "revenue",
    question: "How does Dreambound make money?",
    answer:
      "Dreambound is completely free for you to use! We are supported by schools " +
      "and organizations who pay to advertise on our website, so we can offer all of " +
      "our career resources for free.",
  },
];

export const DreamFAQs = () => {
  const analytics = useMixpanel();
  const [expanded, setExpanded] = useState<string | false>(false);

  const handleChange =
    (questionId: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      void analytics.track("Homepage - Toggle FAQ Accordion", {
        questionId,
      });
      setExpanded(isExpanded ? questionId : false);
    };

  return (
    <DreamFAQContainer>
      {FAQs.map(({ id, question, answer }) => (
        <StyledAccordion
          key={id}
          square={true}
          disableGutters={true}
          expanded={expanded === id}
          onChange={handleChange(id)}
        >
          <AccordionSummary
            expandIcon={
              <ExpandIcon>
                <FontAwesomeIcon icon={expanded === id ? faMinus : faPlus} />
              </ExpandIcon>
            }
          >
            <Question>{question}</Question>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>{answer}</Typography>
          </AccordionDetails>
        </StyledAccordion>
      ))}
    </DreamFAQContainer>
  );
};

const DreamFAQContainer = styled("div")`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const StyledAccordion = styled(Accordion)`
  border: none;
  border-bottom: 1px solid #E0E0E0;
  border-radius: 0 !important;
  box-shadow: none !important;
  width: 100%;
  transition: all 0.3s ease;
  margin: 0;
  background: transparent;

  &:before {
    display: none;
  }

  &:first-of-type {
    border-top: 1px solid #E0E0E0;
  }

  & .MuiAccordionSummary-root {
    min-height: 4.5rem;
    padding: 0 0.5rem;

    &.Mui-expanded {
      border-bottom: none;
    }
  }

  & .MuiAccordionDetails-root {
    padding: 0 0.5rem 1.5rem;
  }
`;

const ExpandIcon = styled("span")`
  color: ${paletteOptions.primary.main};
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;
`;

const Question = styled(Typography)`
  font-weight: 700;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  color: ${paletteOptions.text.primary};
`;
